'use client';

import React, { useRef } from 'react';
import { getCdnUrl } from '@/utils/cdn';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { motion, useInView } from 'framer-motion';

export default function SobreClient() {
  const missionRef = useRef(null);
  const isInView = useInView(missionRef, { once: true, margin: '-100px' });

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.5,
      },
    }),
  };

  return (
    <div className="overflow-x-hidden bg-[#F0F0F0]">
      <Navbar />
      
      <div className="2xl:max-w-[1500px] w-[100vw] mx-auto md:px-10 px-0 mb-10 pt-[70px]">
        {/* Header Section with Icons */}
        <div className="flex justify-between">
          <div className="md:flex hidden gap-x-24 px-0 mt-8"></div>
          <div className="md:hidden flex px-6 mt-8"></div>
        </div>

        {/* Main Content */}
        <div className="px-6">
          {/* Hero Section - Quem é a Forbli */}
          <section className="mb-16 relative overflow-visible">
            <img
              src={getCdnUrl("/images/common/decor-ativo-3.webp")}
              alt="Decorativo"
              width={600}
              height={300}
              className="pointer-events-none select-none absolute -left-20 md:-left-32 -top-10 w-[280px] md:w-[400px] h-auto"
              style={{ opacity: 0.25 }}
            />
 
            <div className="grid md:grid-cols-3 gap-12 items-center relative z-10">
              <div className="animate-fade-up md:col-span-1">
                <h1 className="text-[32px] md:text-5xl font-bold text-[#3a3a39] animate-slide-up">
                  Quem é a Forbli Construtora
                </h1>
                <div className="mt-3 h-1 w-16 bg-forbli-green rounded-full animate-slide-up" style={{ animationDelay: '0.2s' }} />
                <p className="mt-5 text-lg text-gray-700 leading-relaxed animate-slide-up" style={{ animationDelay: '0.4s' }}>
                  A Forbli é uma construtora e incorporadora com atuação em Barbacena, São João del-Rei e região, que nasceu do sonho de dois engenheiros que, 
                  após anos de experiência no mercado imobiliário, uniram seus conhecimentos para criar 
                  uma empresa com foco em inovação, qualidade e, acima de tudo, na satisfação de seus clientes.
                </p>
              </div>
              <div className="relative animate-slide-in-right md:col-span-2">
                <div className="relative h-[400px] md:h-[620px] rounded-3xl bg-gray-100 overflow-hidden">
                  <img
                    src={getCdnUrl("/images/about/hero.webp")}
                    alt="Prédio Forbli Construtora em Barbacena e São João del-Rei"
                    className="w-full h-full object-cover animate-rotate-in"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/5" />
                </div>
              </div>
            </div>
          </section>

          {/* Missão, Valores, Visão */}
          <section ref={missionRef} className="mb-16 relative overflow-hidden">
            <div className="pointer-events-none absolute -left-24 top-1/3 w-72 h-72 rounded-full bg-forbli-green/10 blur-3xl" />
            <div className="pointer-events-none absolute -right-24 bottom-0 w-80 h-80 rounded-full bg-forbli-green/10 blur-3xl" />

            <div className="grid md:grid-cols-3 gap-8 relative z-10">
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                custom={0}
                className="bg-[#F0F0F0] rounded-2xl p-8 md:p-10 md:mt-12"
              >
                <div className="text-center mb-8">
                  <div className="inline-block bg-forbli-green text-white px-24 py-4 rounded-full text-xl md:text-3xl font-bold float-y-infinite">
                    Missão
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed text-left text-xl">
                  Fazer com que o <span className="text-forbli-green font-bold">sonho da casa própria</span> se torne uma realidade para mais brasileiros em Barbacena e São João del-Rei, oferecendo <span className="text-forbli-green font-bold">soluções acessíveis e de qualidade</span>.
                </p>
              </motion.div>

              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                custom={1}
                className="bg-[#F0F0F0] rounded-2xl p-8 md:p-10 md:mt-24"
              >
                <div className="text-center mb-8">
                  <div className="inline-block bg-gradient-to-r from-forbli-green to-forbli-lime text-white px-24 py-4 rounded-full text-xl md:text-3xl font-bold float-y-infinite">
                    Valores
                  </div>
                </div>
                <ul className="space-y-5 text-gray-700 text-lg">
                  <li className="flex items-start gap-3">
                    <span className="mt-2 inline-block w-3 h-3 rounded-full bg-forbli-green"></span>
                    <span className="flex-1"><span className="text-forbli-green font-bold">Transparência:</span> Falamos de forma clara e honesta para gerar confiança.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-2 inline-block w-3 h-3 rounded-full bg-forbli-green"></span>
                    <span className="flex-1"><span className="text-forbli-green font-bold">Qualidade:</span> Somos comprometidos com a qualidade de cada projeto.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-2 inline-block w-3 h-3 rounded-full bg-forbli-green"></span>
                    <span className="flex-1"><span className="text-forbli-green font-bold">Compromisso:</span> Trabalhamos com dedicação para realizar sonhos.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-2 inline-block w-3 h-3 rounded-full bg-forbli-green"></span>
                    <span className="flex-1"><span className="text-forbli-green font-bold">Acessibilidade:</span> Criamos oportunidades para quem quer sair do aluguel e conquistar sua casa.</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                custom={2}
                className="bg-[#F0F0F0] rounded-2xl p-8 md:p-10 md:mt-4"
              >
                <div className="text-center mb-8">
                  <div className="inline-block bg-forbli-lime text-white px-24 py-4 rounded-full text-xl md:text-3xl font-bold float-y-infinite">
                    Visão
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed text-left text-xl">
                  Ser reconhecida como uma <span className="text-forbli-green font-bold">referência no mercado imobiliário</span> em Barbacena, São João del-Rei e Região das Vertentes, destacando-se pela <span className="text-forbli-green font-bold">transparência, qualidade e compromisso com a moradia acessível</span>.
                </p>
              </motion.div>
            </div>
          </section>

          {/* Citação Destacada */}
          <section className="mb-24">
            <div className="relative">
              <div className="relative rounded-4xl p-12 md:py-36 text-white text-center overflow-visible bg-forbli-green sm:mx-20">
                <div className="pointer-events-none absolute -left-24 top-1/3 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
                <div className="pointer-events-none absolute -right-24 -bottom-10 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
 
                <blockquote className="relative z-10 text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto font-semibold">
                  Na Forbli®, acreditamos que a casa própria é mais do que um imóvel – é a
                  realização de um sonho e o começo de uma nova história. Trabalhamos
                  todos os dias para transformar esse sonho em realidade para mais
                  brasileiros, oferecendo soluções acessíveis, de qualidade e sem burocracia.
                </blockquote>
 
                <img src={getCdnUrl("/images/common/decor-ativo-2.webp")} alt="Decorativo" width={352} height={264} className="absolute -bottom-24 -right-8 md:-bottom-32 md:-right-14 w-52 md:w-[22rem] h-auto z-20 rotate-180" />
              </div>
            </div>
          </section>

          {/* Nossa missão é clara */}
          <section className="mb-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="relative h-96 rounded-3xl bg-gray-100 overflow-hidden">
                  <img
                    src={getCdnUrl("/images/about/family-montage.webp")}
                    alt="Família feliz no seu novo apartamento Forbli"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/10" />
                </div>
              </div>
              
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-forbli-green">
                  Nossa missão é clara:
                </h2>
                <div className="mt-3 h-1 w-14 bg-forbli-green rounded-full" />
                <p className="mt-5 text-lg text-gray-700 leading-relaxed">
                  Acreditamos que a comunicação clara e transparente é a base para um relacionamento de confiança. 
                  Por isso, estamos sempre disponíveis para tirar suas dúvidas sobre os empreendimentos em Barbacena (Park Europa) e São João del-Rei (Park da Alterosa) e te ajudar a encontrar o imóvel ideal.
                </p>
                
                <h3 className="mt-6 text-xl font-bold text-gray-900">
                  Na Forbli, a gente fala a sua língua.
                </h3>
                <p className="mt-2 text-gray-700 leading-relaxed">
                  Entender e ser entendido. É assim que construímos uma relação de confiança com você. 
                  <span className="text-forbli-green font-bold"> Vem pra Forbli!</span>
                </p>
              </div>
            </div>
          </section>

          {/* Seção de Direitos e Atendimento */}
          <section className="mb-16 mt-36 relative overflow-hidden">
            <div className="pointer-events-none absolute -left-24 top-1/3 w-72 h-72 rounded-full bg-forbli-green/10 blur-3xl" />
            <div className="pointer-events-none absolute -right-24 bottom-0 w-80 h-80 rounded-full bg-forbli-green/10 blur-3xl" />

            <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Acreditamos que <span className="text-forbli-green font-bold">morar bem é um direito</span>, e que conquistar a casa própria em Barbacena ou São João del-Rei deve ser possível para todos. 
                  Nosso atendimento é prático, direto e humano, guiando você em cada etapa da jornada.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Trabalhamos com programas como "Minha Casa, Minha Vida" e outras oportunidades de financiamento 
                  para tornar seu sonho realidade.
                </p>
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Na Forbli®, a gente fala a sua língua, entende o <span className="text-forbli-green font-bold">seu sonho</span> e <span className="text-forbli-green font-bold">constrói</span> com o coração.
                </h3>
                <div className="inline-block bg-gradient-to-r from-forbli-green to-[#16a34a] text-white px-6 py-3 rounded-full font-bold text-lg ring-1 ring-white/20">
                  #VempraForbli
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
